import {
  getExportTemplates,
  createExportTemplate,
  updateExportTemplate,
  deleteExportTemplate,
} from '../api.js';
import { getCurrentUser } from '../auth.js';

const html = String.raw;

// Available fields for requirements and user stories
const AVAILABLE_FIELDS = {
  requirements: [
    { id: 'type', label: 'Typ', default: true },
    { id: 'text', label: 'Text', default: true },
    { id: 'rationale', label: 'Begründung', default: true },
    { id: 'priority', label: 'Priorität', default: true },
    { id: 'categories', label: 'Kategorien', default: true },
    { id: 'tags', label: 'Tags', default: false },
    { id: 'links', label: 'Links', default: false },
    { id: 'epic', label: 'Epic', default: false },
    { id: 'confidence', label: 'Confidence', default: false },
    { id: 'sourceRange', label: 'Quelle', default: true },
  ],
  userStories: [
    { id: 'story', label: 'Story', default: true },
    { id: 'role', label: 'Rolle', default: true },
    { id: 'goal', label: 'Ziel', default: true },
    { id: 'benefit', label: 'Nutzen', default: true },
    { id: 'acceptance', label: 'Akzeptanzkriterien', default: true },
    { id: 'priority', label: 'Priorität', default: true },
    { id: 'categories', label: 'Kategorien', default: false },
    { id: 'links', label: 'Links', default: false },
    { id: 'epic', label: 'Epic', default: false },
    { id: 'sourceRange', label: 'Quelle', default: false },
  ],
};

const FORMAT_OPTIONS_BY_FORMAT = {
  csv: [
    { id: 'delimiter', label: 'Trennzeichen', type: 'select', options: [',', ';', '|'], default: ',' },
    { id: 'encoding', label: 'Zeichenkodierung', type: 'select', options: ['utf-8', 'iso-8859-1'], default: 'utf-8' },
    { id: 'includeHeaders', label: 'Header einbeziehen', type: 'checkbox', default: true },
  ],
  json: [
    { id: 'prettyPrint', label: 'Formatiert (Pretty Print)', type: 'checkbox', default: true },
    { id: 'rootKey', label: 'Root-Schlüssel', type: 'text', default: 'data' },
    { id: 'arrayWrapper', label: 'Als Array', type: 'checkbox', default: true },
  ],
  md: [
    { id: 'includeTableOfContents', label: 'Inhaltsverzeichnis', type: 'checkbox', default: true },
    { id: 'headingLevel', label: 'Überschriftenebene', type: 'number', default: 2, min: 1, max: 6 },
  ],
  xlsx: [],
};

export const renderExportTemplatesView = async (container) => {
  const user = getCurrentUser();
  if (!user) {
    container.innerHTML = html`<div class="card"><p class="muted">Bitte melde dich an.</p></div>`;
    return;
  }

  container.innerHTML = html`
    <div class="card stack">
      <h2>Export Templates</h2>
      <p class="muted">Lade Templates...</p>
    </div>
  `;

  try {
    const data = await getExportTemplates();
    const systemTemplates = data.systemTemplates ?? [];
    const userTemplates = data.userTemplates ?? [];

    const state = {
      systemTemplates,
      userTemplates,
      editingTemplate: null,
      showEditor: false,
    };

    const render = () => {
      container.innerHTML = html`
        <div class="card stack">
          <header class="row" style="justify-content: space-between; align-items: center;">
            <h2>Export Templates</h2>
            <button type="button" id="create-template-btn">+ Neues Template</button>
          </header>

          ${state.showEditor ? renderEditor(state) : ''}

          <div class="templates-layout">
            <section class="stack templates-column">
              <h3>System Templates</h3>
              <p class="muted small">Vordefinierte Templates für Standard-Exporte</p>
              <div class="stack" style="gap: 8px;">
                ${systemTemplates.map(t => renderTemplateCard(t, false)).join('')}
              </div>
            </section>

            <section class="stack templates-column">
              <h3>Meine Templates (${userTemplates.length})</h3>
              <div class="stack" style="gap: 8px;">
                ${userTemplates.length === 0
                  ? '<p class="muted">Noch keine eigenen Templates erstellt.</p>'
                  : userTemplates.map(t => renderTemplateCard(t, true)).join('')
                }
              </div>
            </section>
          </div>
        </div>
      `;

      attachEventListeners(container, state, render);
    };

    render();
  } catch (error) {
    console.error('Failed to load templates', error);
    container.innerHTML = html`
      <div class="card stack">
        <h2>Export Templates</h2>
        <p class="muted">${error.message}</p>
      </div>
    `;
  }
};

const renderTemplateCard = (template, canEdit) => {
  const formatBadge = {
    csv: 'CSV',
    json: 'JSON',
    md: 'MD',
    xlsx: 'Excel',
  }[template.format] ?? template.format.toUpperCase();

  return html`
    <div class="card template-card">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div class="stack" style="gap: 4px; flex: 1;">
          <div class="row" style="gap: 8px; align-items: center;">
            <strong>${template.name}</strong>
            <span class="pill">${formatBadge}</span>
            ${template.isDefault ? '<span class="pill" style="background: #10b981;">Default</span>' : ''}
          </div>
          <p class="muted small">${template.config?.fields?.length ?? 0} Felder konfiguriert</p>
        </div>
        ${canEdit ? html`
          <div class="row" style="gap: 8px;">
            <button type="button" class="ghost-button" data-edit="${template.templateId}">Bearbeiten</button>
            <button type="button" class="ghost-button" style="color: #f87171;" data-delete="${template.templateId}">Löschen</button>
          </div>
        ` : ''}
      </div>
    </div>
  `;
};

const renderEditor = (state) => {
  const template = state.editingTemplate ?? {
    name: '',
    format: 'csv',
    isDefault: false,
    config: {
      fields: [],
      filters: {},
      formatOptions: {},
    },
  };

  const isNew = !template.templateId;

  const currentRequirementFields = template.config?.fields ?? [];
  const currentUserStoryFields = template.config?.userStoryFields ?? [];
  const formatOptions = FORMAT_OPTIONS_BY_FORMAT[template.format] ?? [];

  return html`
    <div class="modal-overlay" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); z-index: 1000; display: flex; align-items: center; justify-content: center;">
      <div class="card" style="max-width: 600px; max-height: 90vh; overflow-y: auto; margin: 20px;">
        <div class="stack">
          <div class="row" style="justify-content: space-between; align-items: center;">
            <h3>${isNew ? 'Neues Template erstellen' : 'Template bearbeiten'}</h3>
            <button type="button" id="close-editor" class="ghost-button">✕</button>
          </div>

          <form id="template-form" class="stack">
            <label>
              Template-Name *
              <input type="text" name="name" value="${template.name}" required placeholder="z.B. Mein Jira Export">
            </label>

            <label>
              Format *
              <select name="format" required ${isNew ? '' : 'disabled'}>
                <option value="csv" ${template.format === 'csv' ? 'selected' : ''}>CSV</option>
                <option value="json" ${template.format === 'json' ? 'selected' : ''}>JSON</option>
                <option value="md" ${template.format === 'md' ? 'selected' : ''}>Markdown</option>
                <option value="xlsx" ${template.format === 'xlsx' ? 'selected' : ''}>Excel (.xlsx)</option>
                <option value="docx" ${template.format === 'docx' ? 'selected' : ''}>Word (.docx)</option>
              </select>
            </label>

            <label style="display: flex; align-items: center; gap: 8px;">
              <input type="checkbox" name="isDefault" ${template.isDefault ? 'checked' : ''}>
              Als Standard-Template für dieses Format setzen
            </label>

            <h4>Anforderungen Felder</h4>
            <p class="muted small">Wähle die Felder für Anforderungen aus:</p>
            <div id="req-fields-container" class="stack" style="gap: 8px; max-height: 200px; overflow-y: auto; border: 1px solid rgba(255,255,255,0.1); padding: 8px; border-radius: 4px;">
              ${AVAILABLE_FIELDS.requirements.map((field, idx) => {
                const existing = currentRequirementFields.find(f => f.sourceField === field.id);
                const enabled = existing?.enabled ?? field.default;
                const targetName = existing?.targetName ?? field.label;
                const order = existing?.order ?? idx + 1;

                return html`
                  <div class="row" style="gap: 8px; align-items: center; padding: 4px;">
                    <input type="checkbox" name="req_field_enabled_${field.id}" ${enabled ? 'checked' : ''}>
                    <input type="text" name="req_field_name_${field.id}" value="${targetName}" placeholder="${field.label}" style="flex: 1;">
                    <input type="number" name="req_field_order_${field.id}" value="${order}" min="1" max="20" style="width: 60px;" title="Reihenfolge">
                  </div>
                `;
              }).join('')}
            </div>

            <h4>User Story Felder</h4>
            <p class="muted small">Wähle die Felder für User Stories aus:</p>
            <div id="us-fields-container" class="stack" style="gap: 8px; max-height: 200px; overflow-y: auto; border: 1px solid rgba(255,255,255,0.1); padding: 8px; border-radius: 4px;">
              ${AVAILABLE_FIELDS.userStories.map((field, idx) => {
                const existing = currentUserStoryFields.find(f => f.sourceField === field.id);
                const enabled = existing?.enabled ?? field.default;
                const targetName = existing?.targetName ?? field.label;
                const order = existing?.order ?? idx + 1;

                return html`
                  <div class="row" style="gap: 8px; align-items: center; padding: 4px;">
                    <input type="checkbox" name="us_field_enabled_${field.id}" ${enabled ? 'checked' : ''}>
                    <input type="text" name="us_field_name_${field.id}" value="${targetName}" placeholder="${field.label}" style="flex: 1;">
                    <input type="number" name="us_field_order_${field.id}" value="${order}" min="1" max="20" style="width: 60px;" title="Reihenfolge">
                  </div>
                `;
              }).join('')}
            </div>

            ${formatOptions.length > 0 ? html`
              <h4>Format-Optionen</h4>
              <div class="stack" style="gap: 8px;">
                ${formatOptions.map(opt => {
                  const value = template.config?.formatOptions?.[opt.id] ?? opt.default;
                  if (opt.type === 'checkbox') {
                    return html`
                      <label style="display: flex; align-items: center; gap: 8px;">
                        <input type="checkbox" name="opt_${opt.id}" ${value ? 'checked' : ''}>
                        ${opt.label}
                      </label>
                    `;
                  } else if (opt.type === 'select') {
                    return html`
                      <label>
                        ${opt.label}
                        <select name="opt_${opt.id}">
                          ${opt.options.map(o => html`<option value="${o}" ${value === o ? 'selected' : ''}>${o}</option>`).join('')}
                        </select>
                      </label>
                    `;
                  } else if (opt.type === 'number') {
                    return html`
                      <label>
                        ${opt.label}
                        <input type="number" name="opt_${opt.id}" value="${value}" min="${opt.min ?? 0}" max="${opt.max ?? 100}">
                      </label>
                    `;
                  } else {
                    return html`
                      <label>
                        ${opt.label}
                        <input type="text" name="opt_${opt.id}" value="${value}">
                      </label>
                    `;
                  }
                }).join('')}
              </div>
            ` : ''}

            <div class="row" style="gap: 8px; justify-content: flex-end;">
              <button type="button" id="cancel-editor" class="ghost-button">Abbrechen</button>
              <button type="submit">${isNew ? 'Template erstellen' : 'Änderungen speichern'}</button>
            </div>
            <p class="muted" id="editor-status"></p>
          </form>
        </div>
      </div>
    </div>
  `;
};

const attachEventListeners = (container, state, render) => {
  // Create template button
  const createBtn = container.querySelector('#create-template-btn');
  if (createBtn) {
    createBtn.addEventListener('click', () => {
      state.editingTemplate = null;
      state.showEditor = true;
      render();
    });
  }

  // Edit template buttons
  container.querySelectorAll('[data-edit]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const templateId = e.currentTarget.dataset.edit;
      const template = state.userTemplates.find(t => t.templateId === templateId);
      if (template) {
        state.editingTemplate = template;
        state.showEditor = true;
        render();
      }
    });
  });

  // Delete template buttons
  container.querySelectorAll('[data-delete]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const templateId = e.currentTarget.dataset.delete;
      const template = state.userTemplates.find(t => t.templateId === templateId);
      if (!template) return;

      if (!confirm(`Template "${template.name}" wirklich löschen?`)) return;

      try {
        await deleteExportTemplate(templateId);
        state.userTemplates = state.userTemplates.filter(t => t.templateId !== templateId);
        render();
      } catch (error) {
        alert(`Fehler beim Löschen: ${error.message}`);
      }
    });
  });

  // Editor close/cancel buttons
  const closeBtn = container.querySelector('#close-editor');
  const cancelBtn = container.querySelector('#cancel-editor');
  [closeBtn, cancelBtn].forEach(btn => {
    if (btn) {
      btn.addEventListener('click', () => {
        state.showEditor = false;
        state.editingTemplate = null;
        render();
      });
    }
  });

  // Editor form submit
  const form = container.querySelector('#template-form');
  const status = container.querySelector('#editor-status');
  if (form && status) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      status.textContent = 'Speichere...';

      try {
        const formData = new FormData(form);
        const name = formData.get('name');
        const format = formData.get('format');
        const isDefault = formData.has('isDefault');

        // Collect requirement fields
        const fields = [];
        AVAILABLE_FIELDS.requirements.forEach((field, idx) => {
          const enabled = formData.has(`req_field_enabled_${field.id}`);
          if (enabled) {
            const targetName = formData.get(`req_field_name_${field.id}`) || field.label;
            const order = parseInt(formData.get(`req_field_order_${field.id}`) || (idx + 1), 10);
            fields.push({
              sourceField: field.id,
              targetName,
              enabled: true,
              order,
            });
          }
        });

        // Collect user story fields
        const userStoryFields = [];
        AVAILABLE_FIELDS.userStories.forEach((field, idx) => {
          const enabled = formData.has(`us_field_enabled_${field.id}`);
          if (enabled) {
            const targetName = formData.get(`us_field_name_${field.id}`) || field.label;
            const order = parseInt(formData.get(`us_field_order_${field.id}`) || (idx + 1), 10);
            userStoryFields.push({
              sourceField: field.id,
              targetName,
              enabled: true,
              order,
            });
          }
        });

        // Collect format options
        const formatOptions = {};
        const optionDefs = FORMAT_OPTIONS_BY_FORMAT[format] ?? [];
        optionDefs.forEach(opt => {
          if (opt.type === 'checkbox') {
            formatOptions[opt.id] = formData.has(`opt_${opt.id}`);
          } else if (opt.type === 'number') {
            formatOptions[opt.id] = parseInt(formData.get(`opt_${opt.id}`) || opt.default, 10);
          } else {
            formatOptions[opt.id] = formData.get(`opt_${opt.id}`) || opt.default;
          }
        });

        const templateData = {
          name,
          format,
          isDefault,
          config: {
            fields,
            userStoryFields,
            filters: {},
            formatOptions,
          },
        };

        if (state.editingTemplate?.templateId) {
          // Update existing
          const updated = await updateExportTemplate(state.editingTemplate.templateId, templateData);
          const idx = state.userTemplates.findIndex(t => t.templateId === state.editingTemplate.templateId);
          if (idx !== -1) {
            state.userTemplates[idx] = updated.template;
          }
        } else {
          // Create new
          const created = await createExportTemplate(templateData);
          state.userTemplates.push(created.template);
        }

        state.showEditor = false;
        state.editingTemplate = null;
        render();
      } catch (error) {
        console.error('Failed to save template', error);
        status.textContent = `Fehler: ${error.message}`;
      }
    });
  }
};
